import React, { useState } from 'react';
import { UserPlus } from 'lucide-react';
import { useTranslation } from '../lib/i18n';
import { saveProfile } from '../lib/profile';

// Forced one-time screen shown right after sign-in if the user hasn't picked
// a display name yet. Calls `onDone(name)` once the name is saved.
const NameOnboarding = ({ onDone }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError('');
    try {
      await saveProfile({ displayName: trimmed });
      onDone(trimmed);
    } catch (err) {
      setError(err.message || 'Failed to save');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center p-6">
      <div className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 p-8 space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300 mb-3">
            <UserPlus size={26} />
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {t('onboarding.nameTitle')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            {t('onboarding.nameHint')}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              {t('settings.profileName')}
            </label>
            <input
              type="text"
              required
              autoFocus
              maxLength={60}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('settings.profileNamePlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy || !name.trim()}
            className="w-full inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium py-2 rounded-lg transition-colors"
          >
            {busy ? t('auth.pleaseWait') : t('onboarding.continue')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default NameOnboarding;
